import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { Button } from "@/libraries/design/components";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DEFAULT_VALUES } from "@/libraries/web-framework/index.ts";

import { TWCLightLogo } from "./twc-light-logo";

export const TeamworkCollectionHero = () => {
    const intl = useIntl();
    const getItFreeLink = useLocalizedHref(
        "/try/cloud/signup?bundle=teamwork-collection&edition=premium"
    );
    const learnMoreLink = useLocalizedHref("/collections/teamwork");

    const secondaryLinkText = intl.formatMessage({
        id: "HomePageTemplateV2.TeamworkCollectionHero.learnMoreButton",
        defaultMessage: "Learn more"
    });

    return (
        <UFOSegment name="homepage-v2-teamworkCollectionHero">
            <section css={sectionStyles}>
                <div css={containerStyles}>
                    <div css={contentWrapperStyles}>
                        <div css={leftColumnStyles}>
                            <TWCLightLogo
                                css={logoStyles}
                                aria-label={intl.formatMessage({
                                    id: "HomePageTemplateV2.TeamworkCollectionHero.logoAriaLabel",
                                    defaultMessage: "Teamwork Collection logo"
                                })}
                            />
                            <h1 className="twc-headline" css={headlineStyles}>
                                <FormattedMessage
                                    id="HomePageTemplateV2.TeamworkCollectionHero.headline"
                                    defaultMessage="The <span>Teamwork</span> platform built for the AI-era"
                                    values={DEFAULT_VALUES}
                                />
                            </h1>
                            <p
                                className="twc-description"
                                css={descriptionStyles}
                            >
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.TeamworkCollectionHero.description",
                                    defaultMessage:
                                        "Get Rovo, Jira, Confluence, and Loom in one collection. Bring AI-powered insights to your projects, knowledge, and video communication."
                                })}
                            </p>
                            <div css={ctaWrapperStyles}>
                                <Button
                                    variant="primary"
                                    href={getItFreeLink}
                                    size="large"
                                    className="twc-get-it-free-button"
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-product-key":
                                            "teamworkCollection",
                                        "data-label-english": "Get it free"
                                    }}
                                >
                                    {intl.formatMessage({
                                        id: "HomePageTemplateV2.TeamworkCollectionHero.getItFreeButton",
                                        defaultMessage: "Get it free"
                                    })}
                                </Button>
                                <Link
                                    type="primary"
                                    bgColor="bgBlackBlue"
                                    className="twc-learn-more-button"
                                    aria-label={secondaryLinkText}
                                    text={secondaryLinkText}
                                    href={learnMoreLink}
                                    dataEventProps={{
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-product-key":
                                            "teamworkCollection",
                                        "data-label-english": "Learn more"
                                    }}
                                />
                            </div>
                        </div>

                        <div css={rightColumnStyles}>
                            <Image
                                src="https://dam-cdn.atl.orangelogic.com/AssetLink/5140xb7orvf7timubceisv3470t0305a.webp"
                                alt={intl.formatMessage({
                                    id: "HomePageTemplateV2.TeamworkCollectionHero.imageAlt",
                                    defaultMessage:
                                        "Teamwork Collection products visualization"
                                })}
                                width="544"
                                height="544"
                                style={{
                                    height: "auto",
                                    aspectRatio: "1/1"
                                }}
                            />
                        </div>
                    </div>
                </div>
            </section>
        </UFOSegment>
    );
};

const sectionStyles = css({
    backgroundColor: wacToken("color.neutral.light0"),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `${token("space.800")} ${token("space.250")}`
});

const containerStyles = css({
    width: "1170px",
    maxWidth: "100%",
    backgroundColor: wacToken("color.primary.black")
});

const contentWrapperStyles = css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: token("space.800"),
    alignItems: "center",

    [media.below.md]: {
        gridTemplateColumns: "1fr",
        gap: token("space.500")
    }
});

const leftColumnStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: token("space.300"),
    padding: `${token("space.600")} ${token("space.400")} ${token(
        "space.600"
    )} ${token("space.800")}`,
    [media.below.md]: {
        padding: token("space.400")
    }
});

const logoStyles = css({
    maxWidth: "100%",
    width: "284px",
    height: "68px",
    aspectRatio: "71/42"
});

const headlineStyles = css({
    margin: 0,
    fontWeight: 700,
    textTransform: "uppercase",
    [`&.twc-headline`]: {
        color: wacToken("color.neutral.light0"),
        fontSize: "3rem",
        lineHeight: 1.1,
        [media.only.md]: {
            fontSize: "2.5rem"
        },
        [media.below.md]: {
            fontSize: "2rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        backgroundImage: `conic-gradient(from 0deg, ${wacToken(
            "color.extended.purple500"
        )} 170deg, ${wacToken(
            "color.primary.yellow"
        )} 171deg 230deg, ${wacToken("color.extended.blue500")} 231deg 360deg)`,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        backgroundPosition: "center -80%",
        backgroundSize: "100% 200%"
    }
});

const descriptionStyles = css({
    fontSize: "1.125rem",
    lineHeight: 1.5,
    margin: 0,
    opacity: 0.9,
    [`&.twc-description`]: {
        color: wacToken("color.neutral.light0")
    },
    [media.below.md]: {
        fontSize: "1rem"
    }
});

const ctaWrapperStyles = css({
    display: "flex",
    gap: token("space.200"),
    flexWrap: "wrap",
    alignItems: "center"
});

const rightColumnStyles = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

TeamworkCollectionHero.displayName = "TeamworkCollectionHero";
